import React, { useCallback } from "react";
import { Form } from "react-final-form";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import type { ThunkDispatchType } from "src/app/reducers/IMainAppState";
import { ForgetPassword } from "src/authentication/actions/PasswordActionCreator";
import {
  EMAIL,
  ForgetPasswordForm,
  type IForgetPasswordFormData,
} from "src/authentication/forms/ForgetPasswordForm";
import { useFormLoadErrors } from "src/shared/forms/hooks/useFormLoadErrors";
import { type IPopupProps, usePopup } from "src/shared/hooks/popups/usePopup";
import { MessagePopup } from "src/shared/popups/MessagePopup";

export type ForgetPasswordPopupProps = IPopupProps<void>;

export const ForgetPasswordPopup = ({
  abortPopup,
  closePopup,
}: ForgetPasswordPopupProps) => {
  const dispatch = useDispatch<ThunkDispatchType>();
  const { openPopup, renderPopup } = usePopup();
  const handleFormSubmit = useCallback(
    async (formData: IForgetPasswordFormData): Promise<void> => {
      await dispatch(ForgetPassword(formData[EMAIL]));

      await openPopup();

      closePopup();
    },
    [closePopup, dispatch, openPopup],
  );
  return (
    <>
      <Form onSubmit={useFormLoadErrors(handleFormSubmit)}>
        {({
          handleSubmit,
          pristine,
          submitting,
          hasValidationErrors,
          hasSubmitErrors,
          modifiedSinceLastSubmit,
        }) => (
          <Modal isOpen toggle={abortPopup}>
            <ModalHeader toggle={abortPopup}>
              <FormattedMessage id="authentication.forget.heading" />
            </ModalHeader>
            <ModalBody>
              <form onSubmit={handleSubmit} id="ForgetPasswordForm">
                <ForgetPasswordForm />
              </form>
            </ModalBody>
            <ModalFooter>
              <Button
                type="submit"
                color="primary"
                form="ForgetPasswordForm"
                disabled={
                  pristine ||
                  submitting ||
                  hasValidationErrors ||
                  (hasSubmitErrors && !modifiedSinceLastSubmit)
                }
              >
                <FormattedMessage id="authentication.forget.submit" />
              </Button>
              <Button type="button" color="light" onClick={abortPopup}>
                <FormattedMessage id="popups.confirmation.cancel" />
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </Form>
      {renderPopup((popupProps) => (
        <MessagePopup
          header={<FormattedMessage id="authentication.forget.ack.heading" />}
          {...popupProps}
        >
          <p>
            <FormattedMessage id="authentication.forget.ack.description" />
          </p>
        </MessagePopup>
      ))}
    </>
  );
};
